<template>
  <div class="p-2">
    <div :class="styles.controls || 'flex gap-2'">
      <DebounceInput
        v-if="searchEnabled"
        :model-value="globalDisplayFilter || ''"
        :placeholder="translations.search_by || 'Search'"
        @update:modelValue="(value) => processFilter(value)"
      />
      <div class="flex gap-2">
        <div v-for="filter in filters" :key="filter.name" class="my-4">
          <select
            class="w-auto rounded p-2 pr-10"
            @change="(value) => processSelect(filter, value)"
          >
            <option value="null">{{ filter.name }}</option>
            <option
              v-for="option in filter.options"
              :key="option[1]"
              :value="option[1]"
            >
              {{ option[0] }}
            </option>
          </select>
        </div>
      </div>
    </div>
    <div :class="styles.container">
      <table class="min-w-full divide-y divide-gray-200">
        <thead :class="styles.header">
          <tr
            v-for="headerGroup in table.getHeaderGroups()"
            :key="headerGroup.id"
          >
            <th
              v-for="header in headerGroup.headers"
              :key="header.id"
              :colSpan="header.colSpan"
              scope="col"
              class="py-3.5 px-3 text-left text-sm font-semibold text-gray-900 first:pl-6 last:pr-6 last:text-right"
              :class="
                header.column.getCanSort() ? 'cursor-pointer select-none' : ''
              "
              @click="
                header.column.getToggleSortingHandler() &&
                header.column.getToggleSortingHandler()($event)
              "
            >
              <FlexRender
                v-if="!header.isPlaceholder"
                :render="header.column.columnDef.header"
                :props="header.getContext()"
              />
              <i
                v-if="header.column.getCanSort()"
                class="fa fa-sort ml-2 text-slate-500"
              />
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100 bg-white">
          <tr
            v-for="row in table.getRowModel().rows"
            :key="row.id"
            :class="styles.rowStyles || 'border-b'"
          >
            <td
              v-for="cell in row.getVisibleCells()"
              :key="cell.id"
              class="py-4 px-3 text-sm text-gray-600 first:pl-6 first:text-blue-900 last:pr-6 last:font-medium"
            >
              <span
                v-html="
                  cell.getValue() ||
                  cell.row.original[cell.column.columnDef.accessorKey] ||
                  cell.row.original[cell.column.columnDef.header]
                "
              ></span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div
      v-if="paginationEnabled && tableData.length > 0"
      class="flex justify-between items-center my-3 border-t border-gray-200 pt-3"
    >
      <button
        class="flex items-center gap-2 px-4 py-2 text-sm text-gray-800 hover:text-gray-900 disabled:text-gray-400 disabled:cursor-not-allowed"
        :disabled="!table.getCanPreviousPage()"
        @click="() => table.previousPage()"
      >
        <i class="fa fa-arrow-left"></i>
        <span class="hidden md:block">
          {{ translations.previous || "Previous" }}
        </span>
      </button>

      <div class="flex items-center gap-4">
        <span v-if="pageIndexEnabled" class="text-sm text-gray-700">
          {{ translations.showing || "Showing" }}
          <strong
            >{{
              table.getState().pagination.pageIndex *
                table.getState().pagination.pageSize +
              1
            }}-{{
              Math.min(
                (table.getState().pagination.pageIndex + 1) *
                  table.getState().pagination.pageSize,
                table.getFilteredRowModel().rows.length,
              )
            }}</strong
          >
          {{ translations.of || "of" }}
          <strong>{{ table.getFilteredRowModel().rows.length }}</strong>
        </span>

        <div class="flex items-center gap-1">
          <button
            v-if="table.getState().pagination.pageIndex > 1"
            class="px-3 py-2 text-sm text-gray-800 border-t-2 border-transparent hover:text-gray-900"
            @click="() => table.setPageIndex(0)"
          >
            1
          </button>

          <span
            v-if="table.getState().pagination.pageIndex > 2"
            class="px-2 text-gray-400"
            >...</span
          >

          <button
            v-if="table.getState().pagination.pageIndex > 0"
            class="px-3 py-2 text-sm text-gray-800 border-t-2 border-transparent hover:text-gray-900"
            @click="
              () =>
                table.setPageIndex(table.getState().pagination.pageIndex - 1)
            "
          >
            {{ table.getState().pagination.pageIndex }}
          </button>

          <button
            class="px-3 py-2 text-sm font-semibold border-t-2 border-gray-800"
          >
            {{ table.getState().pagination.pageIndex + 1 }}
          </button>

          <button
            v-if="
              table.getState().pagination.pageIndex < table.getPageCount() - 1
            "
            class="px-3 py-2 text-sm text-gray-800 border-t-2 border-transparent hover:text-gray-900"
            @click="
              () =>
                table.setPageIndex(table.getState().pagination.pageIndex + 1)
            "
          >
            {{ table.getState().pagination.pageIndex + 2 }}
          </button>

          <span
            v-if="
              table.getState().pagination.pageIndex < table.getPageCount() - 3
            "
            class="px-2 text-gray-400"
            >...</span
          >

          <button
            v-if="
              table.getState().pagination.pageIndex < table.getPageCount() - 2
            "
            class="px-3 py-2 text-sm text-gray-800 border-t-2 border-transparent hover:text-gray-900"
            @click="() => table.setPageIndex(table.getPageCount() - 1)"
          >
            {{ table.getPageCount() }}
          </button>
        </div>
      </div>

      <div class="flex items-center gap-4">
        <div v-if="perPageEnabled" class="flex items-center gap-2">
          <span class="text-sm text-gray-700">{{
            translations.rows_per_page || "Rows per page:"
          }}</span>
          <select
            :value="table.getState().pagination.pageSize"
            class="rounded border-gray-300 text-sm"
            @change="handlePageSizeChange"
          >
            <option
              v-for="pageSize in pageSizes"
              :key="pageSize"
              :value="pageSize"
            >
              {{ pageSize }}
            </option>
          </select>
        </div>

        <button
          class="flex items-center gap-2 px-4 py-2 text-sm text-gray-800 hover:text-gray-900 disabled:text-gray-400 disabled:cursor-not-allowed"
          :disabled="!table.getCanNextPage()"
          @click="() => table.nextPage()"
        >
          <span class="hidden md:block">
            {{ translations.next || "Next" }}
          </span>
          <i class="fa fa-arrow-right"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  FlexRender,
  getCoreRowModel,
  useVueTable,
  createColumnHelper,
  getPaginationRowModel,
  getSortedRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
} from "@tanstack/vue-table";
import { ref } from "vue";
import DebounceInput from "./DebounceInput.vue";
export default {
  components: { FlexRender, DebounceInput },
  props: {
    payload: Array,
    columns: Array,
    styles: {
      type: Object,
      default: () => ({}),
    },
    filters: {
      type: Array,
      required: false,
      default() {
        return [];
      },
    },
    paginationEnabled: {
      type: Boolean,
      default: true,
    },
    perPageEnabled: {
      type: Boolean,
      default: true,
    },
    pageIndexEnabled: {
      type: Boolean,
      default: true,
    },
    searchEnabled: {
      type: Boolean,
      required: false,
      default: true,
    },
    translations: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  data() {
    return {
      table: null,
      tableData: ref(this.payload),
      goToPageNumber: ref(1),
      tsorting: [{ value: {} }],
      pageSizes: [10, 20, 30, 40, 50],
      componentKey: 0,
      componentIndex: 0,
      globalFilter: 0,
      globalDisplayFilter: "",
      selectFilters: {},
      modelValue: null,
      pagination: {
        pageIndex: 0,
        pageSize: this.paginationEnabled ? 20 : 10_000,
      },
    };
  },
  computed: {},
  beforeMount() {
    const params = new URLSearchParams(window.location.search);
    const searchParam = params.get("search");

    if (searchParam) {
      this.globalDisplayFilter = searchParam;
      this.globalFilter += 1;
    }

    this.table = this.renderTable();
  },
  methods: {
    processFilter(value) {
      this.globalDisplayFilter = String(value);
      this.globalFilter += 1;
    },
    processSelect(filter, event) {
      let selected = event.target.options[event.target.selectedIndex];
      if (selected && selected.value !== "null") {
        this.selectFilters[filter.key] = selected.value || selected.text;
      } else {
        delete this.selectFilters[filter.key];
      }
      this.globalFilter += 1;
    },
    renderColumns() {
      const columnHelper = createColumnHelper();
      return this.columns.map((column) => {
        return columnHelper.accessor(column.key, {
          header: column.hidden ? null : column.display,
          accessorKey: column.key,
          footer: (props) => props.column.id,
          enableSorting: column.sort,
          sortingFn: (rowA, rowB, columnId) => {
            const a =
              rowA.original[`raw_${columnId}`] || rowA.getValue(columnId);
            const b =
              rowB.original[`raw_${columnId}`] || rowB.getValue(columnId);
            return a > b ? 1 : a < b ? -1 : 0;
          },
        });
      });
    },
    handlePageSizeChange(e) {
      this.table.setPageSize(Number(e.target.value));
      this.componentKey += 1;
    },
    handleGoToPage(e) {
      const page = e.target.value ? Number(e.target.value) - 1 : 0;
      this.goToPageNumber.value = page + 1;
      this.table.setPageIndex(page);
    },
    filterMatches(rows) {
      let cols = this.table.getAllColumns();
      return (
        cols
          .map((column) => {
            return String(rows.original[column.id])
              .toLowerCase()
              .includes(String(this.globalDisplayFilter).toLowerCase());
          })
          .indexOf(true) >= 0
      );
    },
    selectMatches(rows) {
      return Object.keys(this.selectFilters).every((key) => {
        return (
          this.selectFilters[key] === "" ||
          rows.original[`raw_${key}`] === this.selectFilters[key] ||
          rows.original[key] === this.selectFilters[key]
        );
      });
    },
    renderTable() {
      let that = this;
      return useVueTable({
        get data() {
          return that.tableData;
        },
        state: {
          get sorting() {
            return that.tsorting;
          },
          get pagination() {
            return that.pagination;
          },
          get globalFilter() {
            return that.globalFilter;
          },
        },
        onPaginationChange: (updater) => {
          that.pagination =
            updater instanceof Function ? updater(that.pagination) : updater;
        },
        onSortingChange(updaterOrValue) {
          that.tsorting =
            typeof updaterOrValue === "function"
              ? updaterOrValue(that.tsorting)
              : updaterOrValue;
        },
        onGlobalFilterChange(updateOrValue) {
          // TODO seems this isnt being hit?
          that.globalFilter =
            typeof updaterOrValue === "function"
              ? updaterOrValue(that.globalFilter)
              : updaterOrValue;
        },
        globalFilterFn: (rows, columnId, filterValue) => {
          return that.filterMatches(rows) && that.selectMatches(rows);
        },
        columns: this.renderColumns(),
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues(),
        debugTable: true,
      });
    },
  },
};
</script>
